import type { JSX } from "preact";
import type { Operator } from "../../api";
import type { SettingsTab } from "../../lib/route";
import { accessOf, canSeeTab, guardTab, SETTINGS_GROUPS } from "../../lib/tabs";
import { useI18n, type Lang } from "../../lib/i18n";
import { ProfileTab } from "./ProfileTab";
import { NotificationsTab } from "./NotificationsTab";
import { SecurityTab } from "./SecurityTab";
import { SitesTab } from "./SitesTab";
import { WidgetTab } from "./WidgetTab";
import { CannedTab } from "./CannedTab";
import { TriggersTab } from "./TriggersTab";
import { HoursTab } from "./HoursTab";
import { OperatorsTab } from "./OperatorsTab";
import { SpamTab } from "./SpamTab";
import { AuditTab } from "./AuditTab";
import { BillingTab } from "./BillingTab";

/**
 * Settings, grouped the way people look for them: first what is about you,
 * then what the customer sees, then the team, then the bill. Operators without
 * the admin role can read the site settings but not change them, and do not
 * see the team at all; only the owner sees billing.
 *
 * Which tabs exist and who may open them lives in lib/tabs.ts, so the rule is
 * testable without rendering a screen.
 */

export function SettingsView({
  operator,
  tab,
  onTab,
  onOperatorChange,
  onLang,
}: {
  operator: Operator;
  tab: SettingsTab;
  onTab: (tab: SettingsTab) => void;
  onOperatorChange: (next: Operator) => void;
  onLang: (lang: Lang) => Promise<void>;
}): JSX.Element {
  const { t } = useI18n();
  const access = accessOf(operator.role);
  const isAdmin = access.isAdmin;
  const current = guardTab(tab, access);
  const label = SETTINGS_GROUPS.flatMap((g) => g.tabs).find((x) => x.key === current)?.label(t) ?? "";

  return (
    <div class="settings-layout">
      <nav class="subnav" aria-label={t.settings.sectionsLabel}>
        <h1>{t.settings.title}</h1>
        {SETTINGS_GROUPS.map((group) => (
          <div key={group.title(t)} class="subnav-group">
            <span class="subnav-title">{group.title(t)}</span>
            {group.tabs
              .filter((x) => canSeeTab(x, access))
              .map((x) => (
                <button key={x.key} aria-current={x.key === current ? "page" : undefined} onClick={() => onTab(x.key)}>
                  {x.label(t)}
                </button>
              ))}
          </div>
        ))}
      </nav>

      <div class="settings">
        <h2 class="settings-title">{label}</h2>
        {current === "profile" ? <ProfileTab operator={operator} onChange={onOperatorChange} onLang={onLang} /> : null}
        {current === "notifications" ? <NotificationsTab isAdmin={isAdmin} /> : null}
        {current === "security" ? <SecurityTab /> : null}
        {current === "sites" ? <SitesTab isAdmin={isAdmin} isOwner={access.isOwner} /> : null}
        {current === "widget" ? <WidgetTab isAdmin={isAdmin} /> : null}
        {current === "canned" ? <CannedTab isAdmin={isAdmin} /> : null}
        {current === "triggers" ? <TriggersTab isAdmin={isAdmin} /> : null}
        {current === "hours" ? <HoursTab isAdmin={isAdmin} /> : null}
        {current === "spam" ? <SpamTab isAdmin={isAdmin} /> : null}
        {current === "operators" ? <OperatorsTab operator={operator} /> : null}
        {current === "audit" ? <AuditTab /> : null}
        {current === "billing" ? <BillingTab /> : null}
      </div>
    </div>
  );
}
